import {Component, HostListener, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {
    ApiService,
    AuthService,
    PageService,
    PageType,
    PostService,
    PostType,
    SettingsService,
} from '@gallaghercp/core-frontend';
import {ClientService} from '../../services/client.service';
import {Observable, Subscription} from 'rxjs';
import {BreakpointObserver} from '@angular/cdk/layout';
import {map} from 'rxjs/operators';
import {Router} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {APP_CONFIG} from "@core-app/app.config";

@Component({
    selector: 'core-header',
    templateUrl: './header.component.html',
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {

    user: any;
    isLoggedIn: boolean = false;

    pages$?: Observable<any>;
    page?: PageType;
    post?: PostType;

    openChildNav: any;
    hamburger: boolean = false;
    hamburgerChildNav: boolean = false;
    authenticationType?: string;
    isMobileNav: boolean = false;

    isTopNav$?: Observable<boolean>;
    isTransparentNav?: boolean;
    isTopNavTransparentSticky?: boolean;

    subscriptions: Subscription[] = [];

    // Config variables
    darkMode?: boolean;
    postsInNavEnabled?: boolean;
    postsEnabled?: boolean;
    notificationsEnabled?: boolean;
    searchEnabled?: boolean;
    heroImagesEnabled?: boolean;
    isCollapsed = true;
    isDemoMode: boolean = false;

    logoUrl!: string;
    protected logoInvertedUrl!: string;
    protected logoTransparentUrl!: string;

    constructor(
        protected auth: AuthService,
        protected pages: PageService,
        protected posts: PostService,
        public client: ClientService,
        public settings: SettingsService,
        protected breakpoint: BreakpointObserver,
        protected router: Router,
        protected renderer: Renderer2,
        protected api: ApiService,
        @Inject(DOCUMENT) private document?: Document,
    ) {
    }

    ngOnInit(): void {
        this.isTopNav$ = this.settings.isTopNavigation();

        if (APP_CONFIG.demoMode !== undefined) {
            this.isDemoMode = APP_CONFIG.demoMode;
        }

        this.subscriptions.push(this.settings.getInvertedLogo().subscribe((logoUrl: any) => {
            this.logoInvertedUrl = logoUrl;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.settings.getTransparentLogo().subscribe((logoUrl: any) => {
            this.logoTransparentUrl = logoUrl;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.settings.isTransparentNavigation().subscribe((state: any) => {
            this.isTransparentNav = state;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.settings.showPosts().subscribe((state: any) => {
            this.postsEnabled = state;
        }));

        this.subscriptions.push(this.settings.showNotifications().subscribe((state: any) => {
            this.notificationsEnabled = state;
        }));

        this.subscriptions.push(this.settings.showSearch().subscribe((state: any) => {
            this.searchEnabled = state;
        }));

        this.subscriptions.push(this.settings.showHeroImages().subscribe((state: any) => {
            this.heroImagesEnabled = state;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.settings.showArticleThumbnails().subscribe((state: any) => {
            this.postsInNavEnabled = state;
        }));

        this.subscriptions.push(this.settings.isDarkMode().subscribe((state: any) => {
            this.darkMode = state;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.settings.isDarkModeEnabled().subscribe((state: any) => {
            if (state === false && this.darkMode) {
                this.settings.toggle('isDarkMode');
            }
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.pages.currentPage$!.subscribe((page: PageType) => {
            this.page = page;
            this.post = undefined;
            this.updateLogoUrl();
        }));

        this.subscriptions.push(this.posts.currentPost$!.subscribe((post: PostType) => {
            this.post = post;
            this.page = undefined;
            this.updateLogoUrl();
        }));

        this.resetChildNav();
        this.hamburger = false;
        this.hamburgerChildNav = false;
        this.user = this.auth.getUser();
        this.isLoggedIn = this.auth.isAuthenticated();
        this.client.getAccessType().subscribe((type: any) => {
            this.authenticationType = type;
        });

        this.breakpoint.observe([
            '(max-width: 991px)'
        ]).subscribe(result => {
            this.isMobileNav = result.matches;
        });

        this.subscriptions.push(
            this.api.zoneChangeEmit.subscribe((zone: number | false) => {
                const headers: any = {};
                if (zone) {
                    headers['x-service-zone'] = String(zone);
                }
                this.pages$ = this.pages.getPages(headers).pipe(
                    map((pages: PageType[]) => {
                        pages.forEach((page: PageType) => {
                            this.sortChildren(page.children);
                            if (!this.openChildNav && this.pages.getCurrentPage() && (this.pages.getCurrentPage()?.parentId ?? 0) > 0) {
                                if (page.id === this.pages.getCurrentPage().parentId) {
                                    this.openChildNav = page;
                                }
                            }
                        });
                        return pages;
                    })
                );
            })
        );
    }

    showHeader(): boolean {
        return this.auth.isAuthenticated() || this.authenticationType === 'public';
    }

    trackByFn(idx: number) {
        return idx;
    }

    childNav(page: any) {
        this.openChildNav = page;
    }

    resetChildNav() {
        this.openChildNav = false;
    }

    logout() {
        return this.auth.logout();
    }

    sortChildren(children: PageType[]) {
        children.sort((a, b) => (a.name > b.name) ? 1 : -1);
    }

    isCurrentPage(page: any) {
        return this.pages.getCurrentPage().url === page.url;
    }

    @HostListener('window:scroll') onScroll() {
        const offset = 100;
        const currentPosition = window.scrollY;
        if (this.isTransparentNav) {
            this.isTopNavTransparentSticky = currentPosition > offset;
            this.updateLogoUrl();
        }
    }

    showInMenu(page: any, type: string) {
        if (type === 'no-child') {
            return page.meta.showInMenu == 'true' ||
                page.meta.showInMenu == '\'true\'' ||
                page.meta.showInMenu == true;
        } else if (type === 'has-child') {
            return (page.meta.showInMenu == 'true' || page.meta.showInMenu == '\'true\'' || page.meta.showInMenu == true) && !page.meta.hideNavDropdown
        } else {
            return false;
        }
    }

    isDemoModeAndPostsLink(page: any) {
        return this.isDemoMode && page.url === 'posts';
    }

    getSiteName() {
        return this.client.getSiteName();
    }

    updateLogoUrl(): void {
        this.logoUrl = !this.darkMode ? this.logoInvertedUrl : this.logoTransparentUrl;
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
    }

    public goToMain(elementId: string) {
        const element = document.getElementById(elementId);
        element?.focus();
    }
}
